import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Theme,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { TextLogo } from 'components/TextLogo';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { isIframe } from 'shared/utils';
import { makeStyles } from 'tss-react/mui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVert from '@mui/icons-material/MoreVert';
import { Link, useHistory } from 'react-router-dom';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'shared/ENV';
import { LocationDescriptor } from 'history';
import { useColorScheme } from 'hooks/useColorScheme';
import { useSetStatusBarStyle } from 'hooks/useSetStatusBarStyle';
import { MenuBadge } from './MenuBadge';

type HeaderBarAction = 'noControls' | 'back' | string;

export type HeaderIconAction = {
  label: string;
  to: LocationDescriptor;
  Icon: ReactNode;
  variant?: 'camera';
};

type ActionButtonProps = {
  action?: HeaderBarAction;
  className?: string;
  iconClassName?: string;
  toggleNavDrawer: () => void;
};

export type HeaderAppBarProps = {
  title?: string;
  action?: HeaderBarAction;
  elevation: number;
  iconActions: HeaderIconAction[];
  showMenuActions: boolean;
  toggleOpen: () => void;
  onMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const getAppBgColor = (theme: Theme): string => {
  if (ENV.VITE_ENV === 'staging') {
    return theme.palette.mode === 'dark' ? '#006B00' : '#008000';
  }

  return theme.palette.background.headerAppBar;
};

const useStyles = makeStyles<{
  isDarkMode: boolean;
}>()((theme, { isDarkMode }) => {
  const appBgColor = getAppBgColor(theme);

  const darkModeColor = isDarkMode ? theme.palette.primary.main : null;

  return {
    menuButton: {
      marginRight: theme.spacing(1),
      // hard-coding to prevent iOS Dynamic Type from resizing this
      width: 48,
      height: 48,
    },
    menuButtonHamburger: {
      color: theme.palette.text.secondary,
    },
    menuIcon: {
      width: 24,
      height: 24,
    },
    appBarMiddle: {
      flexGrow: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      // Absolute position centers the logo on the toolbar centerline regardless of which
      // side controls render. `pointer-events` lets the gutter pass clicks through to the
      // controls beneath; pair with `space-between` on the Toolbar (see `appBar` below) so
      // leading and trailing controls hug the edges.
      [theme.breakpoints.down('laptop')]: {
        position: 'absolute',
        left: 0,
        right: 0,
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        justifyContent: 'center',
        textAlign: 'center',
        pointerEvents: 'none',
        '& > *': {
          pointerEvents: 'auto',
        },
      },
    },
    title: {
      fontSize: getResponsiveFontSize(theme.typography.pxToRem(20)),
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    textLogo: {
      // hard-coding to prevent iOS Dynamic Type from resizing this
      width: 144, // 9rem
      fill: darkModeColor ?? theme.palette.text.primary,
    },
    appBar: {
      flexGrow: 0,
      // Prevents a long-press on the header from starting a text selection.
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      userSelect: 'none',
      paddingLeft: `env(safe-area-inset-left)`,
      paddingRight: `env(safe-area-inset-right)`,
      paddingTop: `env(safe-area-inset-top)`,
      position: 'sticky',
      backgroundColor: `${appBgColor} !important`,
      // AppBar uses Paper; elevation overlays tint via `background-image` and differ by color scheme.
      backgroundImage: 'none',
      // Default AppBar uses primary.contrastText (white). Light header is brand/staging green — force body ink for icons and title.
      ...(!isDarkMode && { color: `${theme.palette.text.primary} !important` }),
      ...(darkModeColor !== null && { color: `${darkModeColor} !important` }),
      top: 0,
      zIndex: theme.zIndex.drawer + 3,
      // Counterpart to the absolute middle slot: keep leading and trailing controls anchored
      // to opposite edges once the middle leaves the flex flow.
      [theme.breakpoints.down('laptop')]: {
        '& .MuiToolbar-root': {
          justifyContent: 'space-between',
        },
      },
    },
  };
});

const ActionButton = (props: ActionButtonProps): JSX.Element | null => {
  const { action, className, iconClassName, toggleNavDrawer } = props;
  const history = useHistory();
  const { t } = useTranslation();

  if (!action) {
    return (
      <IconButton
        edge="start"
        className={className}
        color="inherit"
        aria-label={t('header.menu')}
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={toggleNavDrawer}
        size="large"
      >
        <MenuBadge />
      </IconButton>
    );
  }

  if (action === 'noControls') {
    return null;
  }

  return (
    <IconButton
      edge="start"
      className={className}
      color="inherit"
      aria-label={t('header.back')}
      aria-controls="menu-appbar"
      aria-haspopup="true"
      onClick={() => {
        // We can't always allow action="back" because that could take the user out of Watch Duty. For now, we're opting into this with a special URL #hash
        // @see https://trello.com/c/Ywn5UB9H/279-refactor-page-history-mechanism
        if (action === 'back' || window.location.hash === '#allow-back') {
          if (history.length > 1) {
            history.goBack();
            return;
          }

          // Fallback if there's no previous entry in the history stack
          history.push('/');
          return;
        }

        history.push(action);
      }}
      size="large"
    >
      <ArrowBackIcon className={iconClassName} />
    </IconButton>
  );
};

export const HeaderAppBar = ({
  title,
  action,
  elevation,
  iconActions,
  showMenuActions,
  toggleOpen,
  onMenuClick,
}: HeaderAppBarProps): JSX.Element => {
  const theme = useTheme();
  const { isDarkMode } = useColorScheme();
  const { classes, cx } = useStyles({ isDarkMode });
  const { t } = useTranslation();

  useSetStatusBarStyle(getAppBgColor(theme));

  return (
    <AppBar className={classes.appBar} elevation={elevation}>
      <Toolbar>
        {(!isIframe() || Boolean(action)) && (
          <ActionButton
            action={action}
            className={cx(
              classes.menuButton,
              !action && classes.menuButtonHamburger,
            )}
            iconClassName={classes.menuIcon}
            toggleNavDrawer={toggleOpen}
          />
        )}

        <Box className={classes.appBarMiddle}>
          {title !== undefined ? (
            <Typography variant="h6" className={classes.title}>
              {title}
            </Typography>
          ) : (
            <TextLogo className={classes.textLogo} />
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          {iconActions.length > 0 && (
            <Stack direction="row" spacing={2}>
              {iconActions.map(({ to, label, Icon, ...rest }) => {
                return (
                  <IconButton
                    key={`${to}-${label}`}
                    to={to}
                    component={Link}
                    aria-label={label}
                    {...rest}
                  >
                    {Icon}
                  </IconButton>
                );
              })}
            </Stack>
          )}

          {showMenuActions && (
            <IconButton
              onClick={onMenuClick}
              size="medium"
              aria-label={t('header.moreButtonLabel')}
            >
              <MoreVert sx={{ width: 24, height: 24 }} />
            </IconButton>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
